import { formatArticleDate } from '@archetelos/lib/date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth/useAuth';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { assetQueries, type AssetListItem } from '@/lib/queries/assets';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/creator/assets')({
  component: AssetsRoute,
});

function AssetsRoute() {
  const { loading } = useRequireRole(['creator'], { creatorStatus: 'approved' });
  const { user, profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [scopeAll, setScopeAll] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'unused'>('all');
  const [selected, setSelected] = useState<AssetListItem | null>(null);

  // NOTE: assets are anon-readable by design (Increment 2 — the SSG build needs
  // them). Scoping to own vs. all is a QUERY FILTER, not a security boundary.
  const list = useQuery(assetQueries.list({ userId: user?.id, all: scopeAll && isAdmin }));

  const rows = useMemo(() => {
    let r = list.data ?? [];
    const q = search.trim().toLowerCase();
    if (q)
      r = r.filter((a) =>
        [a.altText, a.caption ?? '', a.credit ?? ''].some((f) => f.toLowerCase().includes(q)),
      );
    if (filter === 'unused') r = r.filter((a) => a.usageCount === 0);
    return r;
  }, [list.data, search, filter]);

  if (loading) return null;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl text-ink">Image library</h1>
        {isAdmin && (
          <label className="flex items-center gap-2 font-sans text-sm text-ink/70">
            <input
              type="checkbox"
              checked={scopeAll}
              onChange={(e) => setScopeAll(e.target.checked)}
              className="text-oxblood focus:ring-oxblood"
            />
            All contributors
          </label>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by alt, caption, or credit…"
          className="min-w-[220px] flex-1 rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
        <div className="flex gap-1">
          {(['all', 'unused'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1.5 text-sm ${
                filter === f ? 'bg-ink/10 font-medium text-ink' : 'text-ink/60 hover:text-ink'
              }`}
            >
              {f === 'all' ? 'All' : 'Unused'}
            </button>
          ))}
        </div>
      </div>

      {list.isLoading ? (
        <p className="mt-10 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-10 text-center font-sans text-sm text-ink/50">
          {filter === 'unused' ? 'No unused images.' : 'No images yet.'}
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {rows.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setSelected(a)}
              className="group overflow-hidden rounded-md border border-ink/10 text-left hover:border-oxblood"
            >
              <img src={a.publicUrl} alt={a.altText} className="aspect-video w-full object-cover" />
              <div className="p-2">
                <p className="truncate font-sans text-xs text-ink/70">{a.caption ?? a.altText}</p>
                <p className="mt-0.5 font-sans text-[0.7rem] text-ink/45">
                  {a.usageCount === 0
                    ? 'Unused'
                    : `Used in ${a.usageCount} ${a.usageCount === 1 ? 'essay' : 'essays'}`}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <AssetDetail asset={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function AssetDetail({ asset, onClose }: { asset: AssetListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [alt, setAlt] = useState(asset.altText);
  const [caption, setCaption] = useState(asset.caption ?? '');
  const [credit, setCredit] = useState(asset.credit ?? '');
  const usage = useQuery(assetQueries.usage(asset.id));

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['assets'] });

  const save = useMutation({
    mutationFn: async () => {
      if (!alt.trim()) throw new Error('Alt text is required.');
      const { error } = await supabase
        .from('assets')
        .update({
          alt_text: alt.trim(),
          caption: caption.trim() || null,
          credit: credit.trim() || null,
        })
        .eq('id', asset.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Saved. Published pages update on the next rebuild.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  const remove = useMutation({
    mutationFn: async () => {
      // Delete the row (content_assets cascades), then the storage object so the
      // bucket doesn't accumulate unreachable files.
      const { data: row } = await supabase
        .from('assets')
        .select('storage_path')
        .eq('id', asset.id)
        .single();
      const { error } = await supabase.from('assets').delete().eq('id', asset.id);
      if (error) throw error;
      if (row?.storage_path) {
        const { error: sErr } = await supabase.storage
          .from('content-images')
          .remove([row.storage_path]);
        if (sErr) console.warn('[assets] row deleted but storage object remains', sErr);
      }
    },
    onSuccess: () => {
      toast.success('Image deleted.');
      onClose();
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  const essays = usage.data ?? [];
  const onDelete = () => {
    // Delete-with-consequence: name what breaks before it breaks.
    const msg =
      essays.length > 0
        ? `This image appears in ${essays.length} ${
            essays.length === 1 ? 'essay' : 'essays'
          }:\n\n${essays.map((e) => `• ${e.title}`).join('\n')}\n\nDeleting it removes those figures on the next rebuild. Continue?`
        : 'Delete this image? It isn’t used in any essay.';
    if (window.confirm(msg)) remove.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6">
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-lg border border-ink/10 bg-parchment p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl text-ink">Image details</h2>
          <button
            type="button"
            onClick={onClose}
            className="font-sans text-sm text-ink/50 hover:text-oxblood"
          >
            Close
          </button>
        </div>

        <img
          src={asset.publicUrl}
          alt={asset.altText}
          className="mt-4 max-h-64 w-full rounded-md object-contain"
        />

        <label className="mt-4 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          Alt text (required)
        </label>
        <input
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
        <label className="mt-3 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          Caption
        </label>
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
        <label className="mt-3 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          Credit
        </label>
        <input
          value={credit}
          onChange={(e) => setCredit(e.target.value)}
          placeholder="Provenance / attribution"
          className="mt-1 w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />

        <div className="mt-5">
          <p className="font-sans text-xs uppercase tracking-[0.12em] text-ink/60">Used in</p>
          {essays.length === 0 ? (
            <p className="mt-1 font-sans text-sm text-ink/50">Not used in any essay.</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {essays.map((e) => (
                <li key={e.id}>
                  <Link
                    to="/creator/content/$contentId/edit"
                    params={{ contentId: e.id }}
                    className="font-sans text-sm text-oxblood hover:underline"
                  >
                    {e.title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-4 font-sans text-xs text-ink/45">
          Added {formatArticleDate(new Date(asset.createdAt))}
        </p>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={remove.isPending}
            onClick={onDelete}
            className="rounded-md border border-signal px-4 py-2 font-sans text-sm font-medium text-signal hover:bg-signal/10 disabled:opacity-60"
          >
            Delete
          </button>
          <button
            type="button"
            disabled={save.isPending || !alt.trim()}
            onClick={() => save.mutate()}
            className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
